/**
 * 员工管理页 ADM-25（staff-rbac-wechat-login:SC-42~SC-46）。
 *
 * 对齐原型 prototype/admin/src/pages/org/StaffPage.tsx：
 * - 筛选区：姓名 / 岗位 / 微信绑定状态 + 查询/重置
 * - 列表：编号、姓名、岗位、手机号（后端脱敏）、授权校区、微信绑定状态、状态、操作（编辑/授权/停用启用）
 * - 详情抽屉：基本信息、岗位、授权校区、微信绑定信息；编辑/授权/生成绑定二维码/解绑入口
 *   （账号密码登录已由 admin-wechat-qr-login 下线，重置密码入口移除，微信绑定即登录凭证）
 * - 新增对话框：姓名 / 手机号 / 岗位
 * 手机号由后端脱敏返回，前端直接展示。
 */
import { useCallback, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DraggableModal } from "../../components/DraggableModal";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EllipsisLink } from "../../components/EllipsisCell";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import { type CampusItem } from "../../api/campuses.api";
import { resolveDefaultCampusId, useCampusOptions } from "../../hooks/useCampusOptions";
import { useGlobalCampusScope } from "../../auth/GlobalCampusContext";
import {
  positionOptionsForCreate,
  staffApi,
  staffPositionLabel,
  staffStatusLabel,
  weekdayLabel,
  type CreateStaff,
  type StaffDetail,
  type StaffListItem,
  type StaffPosition,
  type StaffStatus,
  type WechatBindStatus,
} from "../../api/staff.api";
import { roleApi, type RoleDetail } from "../../api/role.api";
import { ApiError } from "../../api/transport";
import { StaffEditModal, type StaffEditTab } from "./components/StaffEditModal";
import { StaffGrantModal } from "./components/StaffGrantModal";
import { StaffWechatBindModal } from "./components/StaffWechatBindModal";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";
import { useSession } from "../../auth/SessionContext";

const { Title, Text } = Typography;

/** 岗位选项（从标签映射派生） */
const positionOptions = (
  Object.keys(staffPositionLabel) as StaffPosition[]
).map((value) => ({
  value,
  label: staffPositionLabel[value],
}));

/** 员工状态标签 */
function StaffStatusTag({ status }: { status: StaffStatus }) {
  return (
    <Tag color={status === "ACTIVE" ? "green" : "default"}>
      {staffStatusLabel[status] ?? status}
    </Tag>
  );
}

/** 微信绑定状态标签 */
function WechatStatusTag({ status }: { status: WechatBindStatus }) {
  return (
    <Tag color={status === "BOUND" ? "green" : "orange"}>
      {status === "BOUND" ? "已绑定" : "未绑定"}
    </Tag>
  );
}

/** 岗位列表转中文，顿号分隔 */
function joinPositions(positions: StaffPosition[]): string {
  return positions.map((p) => staffPositionLabel[p] ?? p).join("、");
}

/** 列表授权校区展示：优先 campusNames，缺省时用 campusGrants 对照校区名。 */
function authorizedCampusLabel(
  record: StaffListItem,
  campusList: CampusItem[],
): string {
  if (record.campusNames && record.campusNames.length > 0) {
    return record.campusNames.join("、");
  }
  const names = (record.campusGrants ?? [])
    .map((g) => campusList.find((c) => c.id === g.campusId)?.name)
    .filter((n): n is string => Boolean(n));
  if (names.length > 0) return [...new Set(names)].join("、");
  if ((record.campusGrants?.length ?? 0) > 0) {
    return `${record.campusGrants!.length} 个校区`;
  }
  return "未授权";
}

/** 提取错误信息 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function StaffPage() {
  const { message } = App.useApp();
  const { permissions } = useSession();
  const [data, setData] = useState<StaffListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<{
    name?: string;
    position?: StaffPosition;
    wechatStatus?: WechatBindStatus;
  }>({});

  // 详情/弹窗状态
  const [activeStaff, setActiveStaff] = useState<StaffDetail | null>(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<{
    staff: StaffDetail;
    initialTab: StaffEditTab;
  } | null>(null);
  const [grantingStaff, setGrantingStaff] = useState<StaffDetail | null>(null);
  const [bindingStaff, setBindingStaff] = useState<StaffDetail | null>(null);
  const [createForm] = Form.useForm<CreateStaff>();

  // 新建员工表单重置挪到开窗后：弹窗带 destroyOnHidden，关窗态调 form 方法
  // 会触发「useForm not connected」开发告警
  useEffect(() => {
    if (creating) createForm.resetFields();
  }, [creating, createForm]);

  // 校区选项（授权弹窗/新建员工授权校区用）经共享 hook：ACTIVE + 按数据范围过滤 + numericCode 排序，收编自拉口径
  const { campuses, loading: campusLoading } = useCampusOptions();
  const { scope } = useGlobalCampusScope();
  const defaultCampusId = resolveDefaultCampusId(permissions, scope);
  const [roles, setRoles] = useState<RoleDetail[]>([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await staffApi.listStaffs({ ...filters, page, pageSize });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, message]);

  // 加载角色（用于授权弹窗，一次即可）；校区选项已改经共享 hook useCampusOptions
  useEffect(() => {
    void (async () => {
      try {
        const roleRes = await roleApi.listRoles();
        setRoles(roleRes);
      } catch {
        // 授权弹窗数据加载失败不阻塞列表
      }
    })();
  }, []);

  // 新建员工弹窗打开且校区选项就绪后回填默认授权校区（非总部单校区授权或总部锁定具体校区时预选）：
  // 仅在字段为空时写入不覆盖用户已选；默认校区不在 ACTIVE 选项中时不预选
  useEffect(() => {
    if (
      creating &&
      !campusLoading &&
      defaultCampusId &&
      campuses.some((c) => c.id === defaultCampusId) &&
      !createForm.getFieldValue("campusId")
    ) {
      createForm.setFieldValue("campusId", defaultCampusId);
    }
  }, [creating, campusLoading, campuses, defaultCampusId, createForm]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  /** 仅拉取员工详情，不打开抽屉。 */
  async function fetchStaffDetail(id: string): Promise<StaffDetail | null> {
    try {
      return await staffApi.getStaff(id);
    } catch (err) {
      message.error(errorMessage(err));
      return null;
    }
  }

  /** 拉取员工详情并打开抽屉。 */
  async function openDetail(id: string) {
    setActiveLoading(true);
    try {
      const detail = await fetchStaffDetail(id);
      if (detail) setActiveStaff(detail);
    } finally {
      setActiveLoading(false);
    }
  }

  /** 刷新当前详情（操作后回填） */
  async function refreshActive() {
    if (!activeStaff) return;
    try {
      const detail = await staffApi.getStaff(activeStaff.id);
      setActiveStaff(detail);
    } catch (err) {
      message.error(errorMessage(err));
    }
  }

  /** 提交新建员工 */
  async function submitCreate() {
    try {
      const values = await createForm.validateFields();
      await staffApi.createStaff(values);
      message.success("已新建员工");
      setCreating(false);
      createForm.resetFields();
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError) message.error(err.message);
    }
  }

  /** 停用/启用 */
  async function toggleStatus(staff: StaffListItem | StaffDetail) {
    const action = staff.status === "ACTIVE" ? "suspendStaff" : "activateStaff";
    const verb = staff.status === "ACTIVE" ? "停用" : "启用";
    try {
      await staffApi[action](staff.id);
      message.success(`已${verb}员工`);
      void fetchList();
      if (activeStaff?.id === staff.id) void refreshActive();
    } catch (err) {
      message.error(errorMessage(err));
    }
  }

  /** 生成微信绑定二维码（打开扫码绑定 Modal） */
  function generateQrcode(staff: StaffDetail) {
    setBindingStaff(staff);
  }

  /** 解绑微信 */
  async function unbindWechat(staff: StaffDetail) {
    try {
      await staffApi.unbindWechat(staff.id);
      message.success("已解除微信绑定");
      if (activeStaff?.id === staff.id) void refreshActive();
    } catch (err) {
      message.error(errorMessage(err));
    }
  }

  // 员工写操作统一 ADM-25 EDIT 闸控（对齐后端 @Roles）：行操作、抽屉编辑/授权/停用、微信绑定与排课时段
  const canManageStaff = useHasPermission("ADM-25", "EDIT");
  const { columns, components } = useResizableColumns<StaffListItem>([
    {
      title: "员工编号",
      dataIndex: "staffNo",
      width: 150,
      ellipsis: true,
      render: (v: string | null | undefined, record: StaffListItem) => (
        <EllipsisLink
          value={v ?? record.id.slice(0, 8)}
          onClick={() => void openDetail(record.id)}
        />
      ),
    },
    { title: "姓名", dataIndex: "name", width: 100 },
    {
      title: "岗位",
      dataIndex: "positions",
      width: 160,
      render: (v: StaffPosition[]) => joinPositions(v),
    },
    {
      title: "联系手机号",
      dataIndex: "mobile",
      width: 140,
      render: (v?: string | null) => v ?? "-",
    },
    {
      title: "授权校区",
      dataIndex: "campusNames",
      ellipsis: true,
      render: (_: unknown, record: StaffListItem) =>
        authorizedCampusLabel(record, campuses),
    },
    {
      title: "可排课时段摘要",
      dataIndex: "weeklyAvailability",
      ellipsis: true,
      render: (v: StaffListItem["weeklyAvailability"]) =>
        v?.length ? (
          <Text style={{ fontSize: 12 }}>
            {v
              .map(
                (s) =>
                  `${weekdayLabel[s.dayOfWeek]} ${s.startTime}-${s.endTime}`,
              )
              .join("；")}
          </Text>
        ) : (
          <Text type="secondary">未设置</Text>
        ),
    },
    {
      title: "不可排课日期摘要",
      dataIndex: "availabilityExceptions",
      ellipsis: true,
      render: (v: StaffListItem["availabilityExceptions"]) =>
        v?.length ? (
          <Text style={{ fontSize: 12 }}>
            {v
              .map((e) =>
                e.allDay || !e.startTime || !e.endTime
                  ? e.date
                  : `${e.date} ${e.startTime}-${e.endTime}`,
              )
              .join("；")}
          </Text>
        ) : (
          <Text type="secondary">无</Text>
        ),
    },
    {
      title: "微信绑定状态",
      dataIndex: "qrBindStatus",
      width: 100,
      render: (v: WechatBindStatus) => <WechatStatusTag status={v} />,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (v: StaffStatus) => <StaffStatusTag status={v} />,
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 160,
      render: (v?: string | null) =>
        v ? new Date(v).toLocaleString("zh-CN") : "-",
    },
    {
      title: "操作",
      fixed: "right",
      key: "action",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          {canManageStaff && (
            <Button
              type="link"
              size="small"
              onClick={async () => {
                const detail = await fetchStaffDetail(record.id);
                if (detail) setEditing({ staff: detail, initialTab: "basic" });
              }}
            >
              编辑
            </Button>
          )}
          {canManageStaff && (
            <Button
              type="link"
              size="small"
              onClick={async () => {
                const detail = await fetchStaffDetail(record.id);
                if (detail) setGrantingStaff(detail);
              }}
            >
              授权
            </Button>
          )}
          {canManageStaff && (
            <Popconfirm
              title={
                record.status === "ACTIVE"
                  ? "确认停用该员工？"
                  : "确认启用该员工？"
              }
              onConfirm={() => void toggleStatus(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger={record.status === "ACTIVE"}
              >
                {record.status === "ACTIVE" ? "停用" : "启用"}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]);

  return (
    <div>
      <Title level={4}>员工管理</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="姓名"
            allowClear
            value={filters.name ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, name: e.target.value || undefined }))
            }
            style={{ width: 180 }}
          />
          <Select
            placeholder="岗位"
            allowClear
            value={filters.position}
            onChange={(val) =>
              setFilters((f) => ({ ...f, position: val ?? undefined }))
            }
            style={{ width: 160 }}
            options={positionOptions}
          />
          <Select
            placeholder="微信绑定状态"
            allowClear
            value={filters.wechatStatus}
            onChange={(val) =>
              setFilters((f) => ({ ...f, wechatStatus: val ?? undefined }))
            }
            style={{ width: 160 }}
            options={[
              { value: "BOUND", label: "已绑定" },
              { value: "UNBOUND", label: "未绑定" },
            ]}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => void fetchList()}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-25 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-25" action="CREATE">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreating(true)}
            >
              新增员工
            </Button>
          </HasPermission>
        </div>
        <Table<StaffListItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        open={!!activeStaff}
        onClose={() => setActiveStaff(null)}
        width={520}
        title={activeStaff ? `员工详情 · ${activeStaff.name}` : "员工详情"}
        loading={activeLoading}
      >
        {activeStaff && (
          <>
            <Descriptions
              size="small"
              column={1}
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="员工编号">
                <Text copyable style={{ wordBreak: "break-all" }}>
                  {activeStaff.staffNo ?? activeStaff.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="姓名">
                {activeStaff.name}
              </Descriptions.Item>
              <Descriptions.Item label="岗位">
                {joinPositions(activeStaff.positions)}
              </Descriptions.Item>
              <Descriptions.Item label="联系手机号">
                {activeStaff.mobile ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="授权校区及角色">
                {activeStaff.campusGrants &&
                activeStaff.campusGrants.length > 0 ? (
                  activeStaff.campusGrants.map((g) => {
                    const campusName =
                      campuses.find((c) => c.id === g.campusId)?.name ??
                      g.campusId;
                    const roleNames = (g.roleIds ?? [])
                      .map((rid) => roles.find((r) => r.id === rid)?.name)
                      .filter(Boolean)
                      .join("、");
                    return (
                      <div key={g.campusId}>
                        {campusName}
                        {roleNames ? `（${roleNames}）` : ""}
                        {g.validTo
                          ? ` · 有效期至 ${g.validTo.slice(0, 10)}`
                          : " · 长期有效"}
                      </div>
                    );
                  })
                ) : (
                  <Text type="secondary">未授权</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="微信绑定信息">
                {activeStaff.wechatBound
                  ? `已绑定 ${activeStaff.wechatNickname ?? ""}${
                      activeStaff.wechatBoundAt
                        ? `（${activeStaff.wechatBoundAt.slice(0, 10)}）`
                        : ""
                    }`
                  : "未绑定"}
              </Descriptions.Item>
              <Descriptions.Item label="每周可排课时段">
                <div>
                  {activeStaff.weeklyAvailability?.length ? (
                    activeStaff.weeklyAvailability.map((slot) => (
                      <div
                        key={slot.id ?? `${slot.dayOfWeek}-${slot.startTime}`}
                      >
                        {weekdayLabel[slot.dayOfWeek]} {slot.startTime}-
                        {slot.endTime}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">未设置</Text>
                  )}
                </div>
                {canManageStaff && (
                  <a
                    onClick={() =>
                      setEditing({
                        staff: activeStaff,
                        initialTab: "availability",
                      })
                    }
                  >
                    修改
                  </a>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="临时不可排课日期">
                <div>
                  {activeStaff.availabilityExceptions?.length ? (
                    activeStaff.availabilityExceptions.map((ex) => (
                      <div key={ex.id ?? ex.date}>
                        {ex.date}（{ex.reason}）
                        {!ex.allDay && ex.startTime && ex.endTime
                          ? ` ${ex.startTime}-${ex.endTime}`
                          : ""}
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">无</Text>
                  )}
                </div>
                {canManageStaff && (
                  <a
                    onClick={() =>
                      setEditing({
                        staff: activeStaff,
                        initialTab: "exceptions",
                      })
                    }
                  >
                    修改
                  </a>
                )}
              </Descriptions.Item>
            </Descriptions>
            <Space wrap>
              {canManageStaff && (
                <Button
                  type="primary"
                  onClick={() =>
                    setEditing({ staff: activeStaff, initialTab: "basic" })
                  }
                >
                  编辑
                </Button>
              )}
              {canManageStaff && (
                <Button onClick={() => setGrantingStaff(activeStaff)}>
                  授权
                </Button>
              )}
              {canManageStaff && (
                <Button onClick={() => generateQrcode(activeStaff)}>
                  {activeStaff.wechatBound
                    ? "更新绑定二维码"
                    : "生成绑定二维码"}
                </Button>
              )}
              {canManageStaff && activeStaff.wechatBound && (
                <Popconfirm
                  title="确认解除微信绑定？"
                  description="解绑后该员工将无法扫码登录管理端，也无法登录员工小程序"
                  onConfirm={() => void unbindWechat(activeStaff)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button color="danger">解绑</Button>
                </Popconfirm>
              )}
            </Space>
          </>
        )}
      </Drawer>

      {/* 编辑对话框 */}
      {editing && (
        <StaffEditModal
          staff={editing.staff}
          initialTab={editing.initialTab}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void fetchList();
            if (activeStaff) void refreshActive();
          }}
        />
      )}

      {/* 授权对话框 */}
      {grantingStaff && (
        <StaffGrantModal
          staff={grantingStaff}
          campuses={campuses}
          roles={roles}
          onClose={() => setGrantingStaff(null)}
          onSaved={() => {
            void fetchList();
            if (activeStaff) void refreshActive();
          }}
        />
      )}

      {/* 微信扫码绑定 Modal */}
      <StaffWechatBindModal
        open={!!bindingStaff}
        staff={bindingStaff}
        onClose={() => setBindingStaff(null)}
        onBound={() => {
          setBindingStaff(null);
          void fetchList();
          if (activeStaff) void refreshActive();
        }}
      />

      {/* 新建员工对话框 */}
      <DraggableModal
        open={creating}
        title="新建员工"
        onCancel={() => setCreating(false)}
        onOk={() => void submitCreate()}
        okText="创建"
        cancelText="取消"
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="姓名"
            name="name"
            rules={[{ required: true, message: "请输入姓名" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="联系手机号"
            name="mobile"
            rules={[
              { required: true, message: "请输入联系手机号" },
              { pattern: /^1\d{10}$/, message: "请输入正确的手机号" },
            ]}
          >
            <Input placeholder="11 位手机号" />
          </Form.Item>
          <Form.Item
            label="岗位"
            name="positions"
            rules={[{ required: true, message: "请选择岗位" }]}
          >
            <Select
              mode="multiple"
              options={positionOptionsForCreate(permissions?.hasAllOrgScope)}
              placeholder="可多选"
            />
          </Form.Item>
          <Form.Item
            label="授权校区"
            name="campusId"
            rules={[{ required: true, message: "请选择授权校区" }]}
          >
            <Select
              placeholder="选择授权校区"
              options={campuses.map((c) => ({ label: c.name, value: c.id }))}
            />
          </Form.Item>
        </Form>
      </DraggableModal>
    </div>
  );
}
